import React, { useEffect, useRef, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { FunnelPlotOutlined } from '@ant-design/icons';
import api from '@api/api';
import { CommonResponseObj } from '@models/common';
import createVerifier from '@models/dashboardRules';
import {
  IDashboardLayoutProps,
  IInstantFinancialProfitabilityEfficiency,
  TLongTermOptionValue,
} from '@models/home';
import DashboardAreaChart from '@pages/Home/components/DashboardAreaChart/DashboardAreaChart';
import DashboardDonutChart from '@pages/Home/components/DashboardDonutChart/DashboardDonutChart';
import MetricCard from '@pages/Home/components/MetricCard/MetricCard';
import TreeGraph from '@pages/Home/components/TreeGraph/TreeGraph';
import dataFlowColors from '@pages/Home/models/dataFlowColors';
import { NODES } from '@pages/Home/models/treeGraph';
import { AppDispatch } from '@store/index';
import { IRootState } from '@store/reducers';
import { setFinancialProfitabilityEfficiency } from '@store/reducers/dashboardRealTimeValueSlice';
import { formatNumberWithDigits } from '@utils/common';
import { Skeleton } from 'antd';
import axios from 'axios';
// import ReactECharts from 'echarts-for-react';
import Highcharts from 'highcharts';
import Highcharts3d from 'highcharts/highcharts-3d';
import cylinder from 'highcharts/modules/cylinder';
import funnel3D from 'highcharts/modules/funnel3d';
import HighchartsReact from 'highcharts-react-official';

import '../BaseLayout.scss';

// 初始化模块
Highcharts3d(Highcharts);
// 初始化cylinder模块
cylinder(Highcharts);
funnel3D(Highcharts);

interface ILongTermData {
  payment: {
    normalOperationMinutesMap: number[]; // 實際可運行時間
    durationMap: number[]; // 充電槍已付款完成總分鐘數
    timeList: string[]; // 時間
  } | null;
  incomeStatistics: {
    moneys: string[]; // 充電收入
    serviceMoneys: string[]; // 充電毛利
    timeList: string[]; // 時間
  } | null;
  orderCountStatistics: {
    bills: number[]; // 總訂單量
    abnormalBills: number[]; // 異常訂單量
    timeList: string[]; // 時間
  } | null;
  chargingPerformance: {
    durationMap: number[];
    normalOperationMinutesMap: number[];
    timeList: string[];
  } | null;
  chargingTurnoverRate: {
    totalBillCountList: number[]; // 總訂單量
    totalGunCountList: number[]; // 總充電槍數
    timeList: string[]; // 時間
  } | null;
  utilization: {
    maximumMinutesMap: number[]; // 最大可運行時間
    normalOperationMinutesMap: number[]; // 實際可運行時間
    utilizationMap: number[]; // 稼動率
    timeList: string[]; // 時間
  } | null;
}

const initLongTermData: ILongTermData = {
  payment: null,
  incomeStatistics: null,
  orderCountStatistics: null,
  chargingPerformance: null,
  chargingTurnoverRate: null,
  utilization: null,
};
const ShortTermGraph = memo(() => (
  <TreeGraph
    connections={[
      // 財務收益
      // 充電車位績效率
      // 充電車位翻桌率
      {
        data: [NODES.PLATFORM(345, 100), NODES.MEMBER(450, 700)],
        direction: 'right',
        color: [dataFlowColors.FinancialProfit],
      },
      {
        data: [NODES.PILE(580, 300), NODES.EV(480, 480)],
        direction: 'left',
        color: [
          dataFlowColors.ChargingStationEfficiency,
          dataFlowColors.ChargingStationTurnoverRate,
        ],
      },
    ]}
  />
));
const LongTermGraph = memo(() => (
  <TreeGraph
    connections={[
      // 財務收益
      // 充電車位績效率
      // 充電訂單良率
      // 充電車位翻桌率
      {
        data: [NODES.PLATFORM(330, 100), NODES.MEMBER(300, 600)],
        direction: 'left',
        color: [dataFlowColors.FinancialProfit],
      },
      {
        data: [NODES.PLATFORM(330, 100), NODES.PILE(560, 300)],
        direction: 'right',
        color: [dataFlowColors.ChargingOrderYieldRateTrend],
      },
      {
        data: [NODES.PLATFORM(330, 100), NODES.PILE(560, 300)],
        direction: 'right',
        color: [dataFlowColors.ChargingOrderYieldRateTrend],
      },
      {
        data: [NODES.PILE(560, 300), NODES.EV(450, 500)],
        direction: 'left',
        color: [
          dataFlowColors.ChargingStationEfficiency,
          dataFlowColors.ChargingStationTurnoverRate,
          dataFlowColors.ChargingOrderYieldRateTrend,
        ],
      },
      {
        data: [NODES.EV(450, 500), NODES.MEMBER(300, 600)],
        direction: 'left',
        color: [dataFlowColors.ChargingOrderYieldRateTrend],
      },
    ]}
  />
));

function FinancialProfitabilityEfficiency({
  activeTab, // 現在選擇的 tab
  activeLongTermOption, // 長期資訊中的時間區間
}: IDashboardLayoutProps) {
  // 該 layout 的 state，如用於存取對應 x, y 的 state，可以是單一值或物件，看 <TreeGraph /> props 要求
  const [instantData, setInstantData] =
    useState<IInstantFinancialProfitabilityEfficiency | null>(null);
  const [longTermData, setLongTermData] =
    useState<ILongTermData>(initLongTermData);
  const [funnelOption, setFunnelOption] = useState<any>(null);
  const commonVerifierRef = useRef(createVerifier());
  const pileCountVerifierRef = useRef(createVerifier());
  const maxDurationVerifierRef = useRef(createVerifier());
  const totalBillCountVerifierRef = useRef(createVerifier());
  const incomeStatisticsMoneyVerifierRef = useRef(createVerifier());
  const timeoutIdRef = useRef<NodeJS.Timeout | boolean | null>(null);
  const controllersRef = useRef([
    axios.CancelToken.source(),
    axios.CancelToken.source(),
    axios.CancelToken.source(),
    axios.CancelToken.source(),
  ]);
  const { t } = useTranslation();
  const sellerId = useSelector((state: IRootState) => state.userSlice.sellerId);
  const basicInfoPileCount = useSelector(
    (state: IRootState) =>
      state.dashboardRealTimeValueSlice.BasicInfo.pileCount,
  );
  const efficiencyTrendPileOutputPower = useSelector(
    (state: IRootState) =>
      state.dashboardRealTimeValueSlice.ChargingEquipmentStability
        ?.efficiencyTrend?.pileOutputPower ?? undefined,
  );
  const dispatch = useDispatch<AppDispatch>();

  // fetch instant data
  async function getSellerChargingPerformance(
    timeSharing: TLongTermOptionValue = 'TODAY',
  ) {
    try {
      const sellerChargingPerformance = await api.post(
        'api/statistics-service/operationView/getSellerChargingPerformance',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );

      const {
        data: { data, code },
      } = sellerChargingPerformance;

      if (code !== 0) {
        console.error('getSellerChargingPerformance failed');
        return;
      }

      if (timeSharing !== 'TODAY') {
        setLongTermData((prevLongTermData: ILongTermData | null) => ({
          ...prevLongTermData!,
          chargingPerformance: data,
        }));
      }

      setInstantData(
        (prevInstantData: IInstantFinancialProfitabilityEfficiency | null) => ({
          ...prevInstantData!,
          chargingPerformance: data,
        }),
      );
    } catch (error) {
      console.error('getSellerChargingPerformance failed');
    }
  }

  async function getSellerPaymentRate(timeSharing: TLongTermOptionValue) {
    try {
      const sellerPaymentRate = await api.post(
        'api/statistics-service/operationView/getSellerPaymentRate',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );

      const {
        data: { data, code },
      } = sellerPaymentRate;

      if (code !== 0) {
        console.error('getSellerPaymentRate failed');
        return;
      }

      setLongTermData((prevLongTermData: ILongTermData | null) => ({
        ...prevLongTermData!,
        payment: data,
      }));
    } catch (error) {
      console.error('getSellerPaymentRate failed');
    }
  }

  async function getSellerOrderCountStatistics(
    timeSharing: TLongTermOptionValue = 'TODAY',
  ) {
    try {
      const sellerOrderCountStatistics = await api.post(
        'api/statistics-service/operationView/getSellerOrderCountStatistics',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );

      const {
        data: { data, code },
      } = sellerOrderCountStatistics;

      if (code !== 0) {
        console.error('getSellerOrderCountStatistics failed');
        return;
      }

      if (timeSharing !== 'TODAY') {
        setLongTermData((prevLongTermData: ILongTermData | null) => ({
          ...prevLongTermData!,
          orderCountStatistics: data,
        }));
      }
    } catch (error) {
      console.error('getSellerOrderCountStatistics failed');
    }
  }

  async function getSellerIncomeStatistics(timeSharing: TLongTermOptionValue) {
    try {
      const sellerIncomeStatistics = await api.post(
        'api/statistics-service/operationView/getSellerIncomeStatistics',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );

      const {
        data: { data, code },
      } = sellerIncomeStatistics;

      if (code !== 0) {
        console.error('getSellerIncomeStatistics failed');
        return;
      }

      if (timeSharing !== 'TODAY') {
        setLongTermData((prevLongTermData: ILongTermData | null) => ({
          ...prevLongTermData!,
          incomeStatistics: data,
        }));
        return;
      }
      setInstantData(
        (prevInstantData: IInstantFinancialProfitabilityEfficiency | null) => ({
          ...prevInstantData!,
          incomeStatistics: data,
        }),
      );
    } catch (error) {
      console.error('getSellerIncomeStatistics failed');
    }
  }

  async function getSellerChargingTurnoverRate(
    timeSharing: TLongTermOptionValue = 'TODAY',
  ) {
    try {
      const sellerChargingTurnoverRate = await api.post(
        'api/statistics-service/operationView/getSellerChargingTurnoverRate',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );

      const {
        data: { data, code },
      } = sellerChargingTurnoverRate;

      if (code !== 0) {
        console.error('getSellerChargingTurnoverRate failed');
        return;
      }

      if (timeSharing !== 'TODAY') {
        setLongTermData((prevLongTermData: ILongTermData | null) => ({
          ...prevLongTermData!,
          chargingTurnoverRate: data,
        }));
      }
    } catch (error) {
      console.error('getSellerChargingTurnoverRate failed');
    }
  }

  async function getSellerUtilization(timeSharing: TLongTermOptionValue) {
    try {
      const sellerUtilization = await api.post(
        'api/statistics-service/operationView/getSellerUtilization',
        {
          sellerId,
          stationId: 0,
          timeSharing,
        },
      );

      const {
        data: { data, code },
      } = sellerUtilization;

      if (code !== 0) {
        console.error('getSellerUtlization failed');
        return;
      }

      setLongTermData((prevLongTermData: ILongTermData | null) => ({
        ...prevLongTermData!,
        utilization: data,
      }));
    } catch (error) {
      console.error('getSellerUtlization failed');
    }
  }

  async function getInstantData() {
    timeoutIdRef.current = true;
    try {
      const payload = { sellerId, stationId: 0, timeSharing: 'TODAY' };
      const controllers = [
        axios.CancelToken.source(),
        axios.CancelToken.source(),
        axios.CancelToken.source(),
        axios.CancelToken.source(),
      ];
      controllersRef.current = controllers;

      const [performanceRes, incomeRes, chargingTurnoverRateRes] =
        await Promise.all([
          api.post<CommonResponseObj>(
            'api/statistics-service/operationView/getSellerChargingPerformance',
            payload,
            { cancelToken: controllers[0].token },
          ),
          api.post<CommonResponseObj>(
            'api/statistics-service/operationView/getSellerIncomeStatistics',
            payload,
            { cancelToken: controllers[2].token },
          ),
          api.post<CommonResponseObj>(
            'api/statistics-service/operationView/getSellerChargingTurnoverRate',
            payload,
            { cancelToken: controllers[3].token },
          ),
        ]);

      setInstantData({
        chargingPerformance: performanceRes.data.data,
        incomeStatistics: incomeRes.data.data,
        chargingTurnoverRate: chargingTurnoverRateRes.data.data,
      });
    } catch (error) {
      console.error('fetch instant data error: ', error);
    }
  }

  useEffect(() => {
    if (
      !instantData ||
      !instantData.chargingPerformance ||
      !instantData.incomeStatistics ||
      !instantData.chargingTurnoverRate
    )
      return;
    const {
      durationMap: [duration],
      normalOperationMinutesMap: [normalOperationMinutes],
    } = instantData.chargingPerformance;
    const {
      moneys: [money],
      serviceMoneys: [serviceMoney],
    } = instantData.incomeStatistics;
    const {
      totalBillCountList: [totalBillCount],
      totalGunCountList: [totalGunCount],
    } = instantData.chargingTurnoverRate;

    dispatch(
      setFinancialProfitabilityEfficiency({
        chargingPerformance: {
          duration,
          normalOperationMinutes,
        },
        incomeStatistics: {
          money,
          serviceMoney,
        },
        chargingTurnoverRate: {
          totalBillCount,
          totalGunCount,
        },
      }),
    );

    commonVerifierRef.current(
      [
        'numerator-cannot-be-greater-than-denominator',
        'numerator-and-denominator-cannot-be-negative',
      ],
      [
        'api/statistics-service/operationView/getSellerChargingPerformance',
        'api/statistics-service/operationView/getSellerIncomeStatistics',
      ],
      [
        [duration, normalOperationMinutes],
        [Number(serviceMoney), Number(money)],
      ],
      [
        ['duration', 'normalOperationMinutes'],
        ['serviceMoney', 'money'],
      ],
    );

    totalBillCountVerifierRef.current(
      ['numerator-and-denominator-cannot-be-negative'],
      ['api/statistics-service/operationView/getSellerChargingTurnoverRate'],
      [[totalBillCount, totalGunCount]],
      [['totalBillCount', 'totalGunCount']],
    );

    if (basicInfoPileCount !== undefined && totalGunCount !== undefined) {
      pileCountVerifierRef.current(
        ['parkingSpaceCount-cannot-be-smaller-than-basicInfoPileCount'],
        [
          [
            'api/statistics-service/operationView/getSellerChargingTurnoverRate',
            '/api/statistics-service/operationView/getStationfacilitiesCountsBySeller',
          ],
        ],
        [[totalGunCount, basicInfoPileCount]],
        [['totalGunCount', 'basicInfoPileCount']],
      );
    }

    // 驗證充電槍總充電時間不能大於充電訂單次數乘以最大充電時長
    const maxDuration = 3 * 60; // 最大充電時長，單位: 分鐘
    maxDurationVerifierRef.current(
      [
        'duration-cannot-be-greater-than-maxDuration_multiplied_by_totalBillCount',
      ],
      [
        [
          'api/statistics-service/operationView/getSellerChargingTurnoverRate',
          'api/statistics-service/operationView/getSellerChargingPerformance',
        ],
      ],
      [[duration, maxDuration * totalBillCount]],
      [['duration', 'maxDuration_times_totalBillCount']],
    );

    // 驗證充電收入不可大於星舟目前最貴電費(10元/度)*充電量(度)
    if (!efficiencyTrendPileOutputPower) return;
    const MOST_EXPENSIVE_ELECTRICITY_FEE = 10; // 星舟目前最貴電費（10 元 / 度）
    const maximumMoney =
      MOST_EXPENSIVE_ELECTRICITY_FEE * efficiencyTrendPileOutputPower;
    incomeStatisticsMoneyVerifierRef.current(
      [
        'incomeStatisticsMoney-cannot-be-greater-than-mostExpensiveElectricityFee-multiplied-by-pileOutputPower',
      ],
      [
        [
          'api/statistics-service/operationView/getSellerIncomeStatistics',
          '/api/statistics-service/operationView/getSellerChargingEfficiency',
        ],
      ],
      [[Number(money), maximumMoney]],
      ['money', 'maximumMoney'],
    );
  }, [instantData]);

  async function recursivelyGetRealtimeData() {
    if (!timeoutIdRef.current) return;

    timeoutIdRef.current = setTimeout(async () => {
      await getInstantData();
      recursivelyGetRealtimeData();
    }, 5000);
  }

  useEffect(() => {
    if (activeTab === 'longTerm' || activeTab === null) return;
    getInstantData();
    recursivelyGetRealtimeData();

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutIdRef.current) {
        if (
          typeof timeoutIdRef.current === 'number' ||
          typeof timeoutIdRef.current === 'object'
        ) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      }
      controllersRef.current.forEach((controller) => {
        controller.cancel('cancel request');
      });
    };
  }, [activeTab]);

  // fetch long term data

  async function getLongTermData(timeSharing: TLongTermOptionValue) {
    await getSellerPaymentRate(timeSharing);
    await getSellerIncomeStatistics(timeSharing);
    await getSellerOrderCountStatistics(timeSharing);
    await getSellerChargingTurnoverRate(timeSharing);
    await getSellerChargingPerformance(timeSharing);
    await getSellerUtilization(timeSharing);
  }

  useEffect(() => {
    if (activeTab === 'longTerm') {
      getLongTermData(activeLongTermOption);
    }
  }, [activeTab, activeLongTermOption]);

  // 整理 funnel 漏斗資料
  function sumArray(array: number[]): number {
    return array.reduce((acc, val) => {
      if (val === 0) {
        return acc;
      }
      return acc + val;
    }, 0);
  }

  function transformFunnelDataIntoFunnelOption(
    utilization: any,
    payment: any,
    chargingPerformance: any,
  ) {
    // init color
    const utilizationLayerColor = '#34457E';
    let chargingPerformanceLayerColor = '#4F79CC';
    let paymentLayerColor = '#4F79CC';

    // utilization
    const sumMaximumMinutes = sumArray(utilization.maximumMinutesMap); // 時間範圍內的最大可運行時間
    const sumNormalOperationMinutesMap = sumArray(
      utilization.normalOperationMinutesMap,
    ); // 時間範圍內的實際可運行時間
    const utilizationRate =
      !utilization.utilizationMap.length ||
      utilization.utilizationMap.length === 0 ||
      sumArray(utilization.utilizationMap) === 0
        ? NaN
        : sumArray(utilization.utilizationMap) /
          utilization.utilizationMap.length; // 時間範圍內的稼動率

    const isUtilizationRateNaN = Number.isNaN(utilizationRate);

    // chargingPerformance
    const sumDurationMap = sumArray(chargingPerformance.durationMap); // 時間範圍內的充電槍總充電時間
    const sumChargingPerformanceNormalOperationMinutesMap =
      sumNormalOperationMinutesMap; // 時間範圍內的充電槍實際可運行時間
    const chargingPerformanceRate =
      (sumDurationMap / sumChargingPerformanceNormalOperationMinutesMap) *
      utilizationRate; // 時間範圍內的充電槍績效率

    // payment
    const paidDuration = sumArray(payment.paidDuration); // 時間範圍內的充電槍已付款完成總分鐘數
    const sumDurationFromPayment = sumArray(payment.totalDuration);
    const paidRate =
      (paidDuration / sumDurationFromPayment) * chargingPerformanceRate; // 時間範圍內的收款良率

    // 檢查顏色，如果 drop rate 介於 1/3 ~ 2/3 間，使用中度警示黃（#F29500）；若 drop rate 大於 2/3，使用高度警示紅（#FF4D4F）
    const chargingPerformanceDropRate =
      (utilizationRate - chargingPerformanceRate) / utilizationRate;
    const paymentDropRate =
      (chargingPerformanceRate - paidRate) / chargingPerformanceRate;

    // 檢查顏色，如果 drop rate 介於 1/3 ~ 2/3 間，使用中度警示黃（#F29500）；若 drop rate 大於 2/3，使用高度警示紅（#FF4D4F）
    if (chargingPerformanceDropRate >= 0.66) {
      chargingPerformanceLayerColor = '#FF4D4F';
    } else if (chargingPerformanceDropRate >= 0.33) {
      chargingPerformanceLayerColor = '#F29500';
    }

    if (paymentDropRate >= 0.66) {
      paymentLayerColor = '#FF4D4F';
    } else if (paymentDropRate >= 0.33) {
      paymentLayerColor = '#F29500';
    }

    const highChartOption = {
      chart: {
        backgroundColor: 'transparent',
        type: 'funnel3d',
        options3d: {
          enabled: false,
          alpha: 10,
          depth: 50,
          axisLabelPosition: 'auto',
          viewDistance: 100,
        },
        spacing: [0, 0, 0, -100],
      },
      title: {
        text: null,
      },
      tooltip: {
        // 使用 formatter 函数显示不同的数据
        formatter(this: Highcharts.TooltipFormatterContextObject) {
          // 在 pointsArray 中找到当前点对应的对象
          // eslint-disable-next-line react/no-this-in-sfc
          switch (this.point.name) {
            case `${t(
              'FinancialProfitabilityEfficiency.ChargingParkingRate',
            )} ${
              isUtilizationRateNaN ? 'NaN' : (utilizationRate * 100).toFixed(0)
            }%`:
              return `${t(
                'FinancialProfitabilityEfficiency.ChargingParkingRate',
              )}: ${(utilizationRate * 100).toFixed(0)}%<br>${t(
                'FinancialProfitabilityEfficiency.MaxOperationTime',
              )}: ${formatNumberWithDigits(sumMaximumMinutes, 0)} ${t(
                'FinancialProfitabilityEfficiency.Minutes',
              )}<br>${t(
                'FinancialProfitabilityEfficiency.ActualOperationTime',
              )}: ${formatNumberWithDigits(
                sumNormalOperationMinutesMap,
                0,
              )} ${t('FinancialProfitabilityEfficiency.Minutes')}`;
              break;
            case `${t(
              'FinancialProfitabilityEfficiency.ParkingPerformanceRate',
            )} ${(chargingPerformanceRate * 100).toFixed(0)}%`:
              return `${t(
                'FinancialProfitabilityEfficiency.ParkingPerformanceRate',
              )}: ${(chargingPerformanceRate * 100).toFixed(1)}%<br>${t(
                'FinancialProfitabilityEfficiency.ActualOperationTime',
              )}: ${formatNumberWithDigits(
                sumNormalOperationMinutesMap,
                0,
              )} ${t('FinancialProfitabilityEfficiency.Minutes')}<br>${t(
                'FinancialProfitabilityEfficiency.GunTotalChargingTime',
              )}: ${formatNumberWithDigits(sumDurationMap, 0)} ${t(
                'FinancialProfitabilityEfficiency.Minutes',
              )}`;
              break;
            case `${t(
              'FinancialProfitabilityEfficiency.ParkingCollectionRate',
            )} ${(paidRate * 100).toFixed(0)}%`:
              return `${t(
                'FinancialProfitabilityEfficiency.ParkingCollectionRate',
              )}: ${(paidRate * 100).toFixed(1)}%<br>${t(
                'FinancialProfitabilityEfficiency.GunTotalChargingTime',
              )}: ${formatNumberWithDigits(sumDurationFromPayment, 0)} ${t(
                'FinancialProfitabilityEfficiency.Minutes',
              )}<br>${t(
                'FinancialProfitabilityEfficiency.PaidGunTotalMins',
              )}: ${formatNumberWithDigits(paidDuration, 0)} ${t(
                'FinancialProfitabilityEfficiency.Minutes',
              )}`;
              break;
            default:
              // eslint-disable-next-line react/no-this-in-sfc
              return `${this.point.name}`;
          }
        },
        style: {
          fontSize: '14px',
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            // 格式化标签以显示在漏斗中心
            format:
              '<b style="margin-right: 24px; font-size: 12px;"}>{point.name}</b>',
            color: 'black', // 文字颜色
            softConnector: true,
            align: 'right',
            x: -23,
            y: -8,
          },
          neckWidth: '0%',
          neckHeight: '0%',
          width: '45%',
          height: '80%',
        },
      },
      legend: {
        itemStyle: {
          fontSize: 16,
        },
      },
      series: [
        {
          name: 'Unique users',
          // 使用假数据使得每一层等大
          data: [
            {
              name: `${t(
                'FinancialProfitabilityEfficiency.ChargingParkingRate',
              )} ${
                isUtilizationRateNaN
                  ? 'NaN'
                  : (utilizationRate * 100).toFixed(0)
              }%`,
              y: 50,
              color: utilizationLayerColor, // 第一層的顏色
            },
            {
              name: `${t(
                'FinancialProfitabilityEfficiency.ParkingPerformanceRate',
              )} ${(chargingPerformanceRate * 100).toFixed(0)}%`,
              y: 30,
              color: chargingPerformanceLayerColor, // 第二層的顏色
            },
            {
              name: `${t(
                'FinancialProfitabilityEfficiency.ParkingCollectionRate',
              )} ${(paidRate * 100).toFixed(0)}%`,
              y: 30,
              color: paymentLayerColor, // 第三層的顏色
            },
          ],
        },
      ],
    };

    return highChartOption;
  }

  useEffect(() => {
    if (
      !longTermData ||
      !longTermData.utilization ||
      !longTermData.chargingPerformance ||
      !longTermData.payment
    )
      return;
    const { utilization, payment, chargingPerformance } = longTermData;
    setFunnelOption(
      transformFunnelDataIntoFunnelOption(
        utilization,
        payment,
        chargingPerformance,
      ),
    );
  }, [
    longTermData?.utilization,
    longTermData?.payment,
    longTermData?.chargingPerformance,
  ]);

  return (
    <div className="BaseLayout">
      {activeTab === 'immediate' && (
        <>
          <ShortTermGraph />
          <MetricCard
            title={t('FinancialProfitabilityEfficiency.ParkingTurnoverRate')}
            position={{ left: '3%', top: '20%' }}
            leftTextArea={{
              subtitle: t('FinancialProfitabilityEfficiency.TotalOrders'),
              value:
                instantData?.chargingTurnoverRate === null ||
                instantData?.chargingTurnoverRate === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantData?.chargingTurnoverRate.totalBillCountList[0],
                      0,
                    ),
            }}
            rightTextArea={{
              subtitle: t(
                'FinancialProfitabilityEfficiency.TotalParkingSpaces',
              ),
              value:
                instantData?.chargingTurnoverRate === null ||
                instantData?.chargingTurnoverRate === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantData?.chargingTurnoverRate.totalGunCountList[0],
                      0,
                    ),
            }}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.OrdersDividedByGuns',
            )}
            ringColor={dataFlowColors.ChargingStationTurnoverRate}
          >
            <DashboardDonutChart
              title={t('FinancialProfitabilityEfficiency.ParkingTurnoverRate')}
              quantifier=""
              value={(() => {
                if (!instantData || !instantData.chargingTurnoverRate)
                  return undefined;
                const { totalBillCountList, totalGunCountList } =
                  instantData.chargingTurnoverRate;
                if (
                  totalBillCountList[0] !== undefined &&
                  totalGunCountList[0] !== undefined
                ) {
                  if (totalGunCountList[0] === 0) return NaN;
                  return Number(
                    formatNumberWithDigits(
                      Number(totalBillCountList[0]) /
                        Number(totalGunCountList[0]),
                      1,
                    ),
                  );
                }
                return undefined;
              })()}
            />
          </MetricCard>
          <MetricCard
            title={t('FinancialProfitabilityEfficiency.FinancialGain')}
            position={{ left: '3%', top: '60%' }}
            leftTextArea={{
              subtitle: t('FinancialProfitabilityEfficiency.Profit'),
              value:
                !instantData || !instantData.incomeStatistics
                  ? undefined
                  : formatNumberWithDigits(
                      Number(instantData.incomeStatistics.serviceMoneys[0]),
                      0,
                    ),
            }}
            rightTextArea={{
              subtitle: t('FinancialProfitabilityEfficiency.Income'),
              value:
                instantData?.incomeStatistics === null ||
                instantData?.incomeStatistics === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      Number(instantData?.incomeStatistics.moneys[0]),
                      0,
                    ),
            }}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.ProfitDividedByIncome',
            )}
            ringColor={dataFlowColors.FinancialProfit}
          >
            <DashboardDonutChart
              title={t('FinancialProfitabilityEfficiency.FinancialGain')}
              value={
                instantData?.incomeStatistics
                  ? (Number(instantData?.incomeStatistics.serviceMoneys[0]) /
                      Number(instantData?.incomeStatistics.moneys[0])) *
                    100
                  : undefined
              }
            />
          </MetricCard>
          <MetricCard
            title={t('FinancialProfitabilityEfficiency.ParkPerformanceRate')}
            position={{ left: '56%', top: '20%' }}
            leftTextArea={{
              subtitle: t('FinancialProfitabilityEfficiency.TotalChargingTime'),
              value:
                instantData?.chargingPerformance === null ||
                instantData?.chargingPerformance === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantData?.chargingPerformance.durationMap[0],
                      0,
                    ),
            }}
            rightTextArea={{
              subtitle: t('FinancialProfitabilityEfficiency.ChargingTime'),
              value:
                instantData?.chargingPerformance === null ||
                instantData?.chargingPerformance === undefined
                  ? undefined
                  : formatNumberWithDigits(
                      instantData?.chargingPerformance
                        .normalOperationMinutesMap[0],
                      0,
                    ),
            }}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.TotalTimeDividedByRunningTime',
            )}
            ringColor={dataFlowColors.ChargingStationEfficiency}
          >
            <DashboardDonutChart
              title={t('FinancialProfitabilityEfficiency.ParkPerformanceRate')}
              value={(() => {
                if (!instantData || !instantData.chargingPerformance)
                  return undefined;
                const { normalOperationMinutesMap, durationMap } =
                  instantData.chargingPerformance;
                if (
                  normalOperationMinutesMap[0] !== undefined &&
                  durationMap[0] !== undefined
                ) {
                  if (Number(normalOperationMinutesMap[0]) === 0) return NaN;
                  const result = formatNumberWithDigits(
                    (durationMap[0] / normalOperationMinutesMap[0]) * 100,
                    0,
                  );
                  return result;
                }
                return undefined;
              })()}
            />
          </MetricCard>
        </>
      )}
      {activeTab === 'longTerm' && (
        <>
          <LongTermGraph />
          <MetricCard
            title={t('FinancialProfitabilityEfficiency.ParkPerformanceRate')}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.TimePeriodIncome',
            )}
            position={{ left: '0%', top: '6%' }}
            ringColor={dataFlowColors.FinancialProfit}
            isLongterm
          >
            <DashboardAreaChart
              range={activeLongTermOption}
              quantifier={t('Common.CurrencyUnit')}
              chartData={
                !longTermData.incomeStatistics
                  ? undefined
                  : {
                      time: longTermData.incomeStatistics.timeList,
                      dataset: [
                        {
                          name: t(
                            'FinancialProfitabilityEfficiency.ChargingIncome',
                          ),
                          data: longTermData.incomeStatistics.moneys?.map(
                            (item) => Number(item),
                          ),
                          color: '#507ACD',
                        },
                      ],
                    }
              }
            />
          </MetricCard>
          <MetricCard
            title={t('FinancialProfitabilityEfficiency.OrderYieldRateTrend')}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.SuccessfulOrdersDividedByTotalOrders',
            )}
            position={{ left: '0%', top: '38%' }}
            ringColor={dataFlowColors.ChargingOrderYieldRateTrend}
            isLongterm
          >
            <DashboardAreaChart
              range={activeLongTermOption}
              chartData={
                !longTermData.orderCountStatistics
                  ? undefined
                  : {
                      time: longTermData.orderCountStatistics.timeList,
                      dataset: [
                        {
                          name: t(
                            'FinancialProfitabilityEfficiency.OrderYieldRate',
                          ),
                          data: longTermData.orderCountStatistics.abnormalBills?.map(
                            (item, idx) =>
                              ((Number(
                                longTermData.orderCountStatistics?.bills[idx],
                              ) -
                                Number(item)) /
                                Number(
                                  longTermData.orderCountStatistics?.bills[idx],
                                )) *
                              100,
                          ),
                          color: '#507ACD',
                        },
                      ],
                    }
              }
            />
          </MetricCard>
          <MetricCard
            title={t(
              'FinancialProfitabilityEfficiency.ParkPerformanceRateTrend',
            )}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.TotalTimeDividedByRunTime',
            )}
            position={{ left: '0%', top: '70%' }}
            ringColor={dataFlowColors.ChargingStationEfficiency}
            isLongterm
          >
            <DashboardAreaChart
              range={activeLongTermOption}
              chartData={
                !longTermData.chargingPerformance
                  ? undefined
                  : {
                      time: longTermData.chargingPerformance.timeList,
                      dataset: [
                        {
                          name: t(
                            'FinancialProfitabilityEfficiency.ParkPerformanceRateTrend',
                          ),
                          data: longTermData?.chargingPerformance?.durationMap?.map(
                            (item, idx) => {
                              const normalOperationMinutesMap =
                                longTermData.chargingPerformance!
                                  .normalOperationMinutesMap[idx];
                              const durationMap = item;
                              return parseInt(
                                Number(
                                  (durationMap / normalOperationMinutesMap) *
                                    100,
                                ).toFixed(0),
                                10,
                              );
                            },
                          ),
                          color: '#507ACD',
                        },
                      ],
                    }
              }
              boundary={1.25}
            />
          </MetricCard>
          <MetricCard
            title={t('FinancialProfitabilityEfficiency.ParkingTurnoverRate')}
            tooltipContent={t(
              'FinancialProfitabilityEfficiency.OrdersDividedByGuns',
            )}
            position={{ left: '55%', top: '52%' }}
            ringColor={dataFlowColors.ChargingStationTurnoverRate}
            isLongterm
          >
            <DashboardAreaChart
              quantifier=""
              range={activeLongTermOption}
              chartData={
                !longTermData.chargingTurnoverRate
                  ? undefined
                  : {
                      time: longTermData.chargingTurnoverRate.timeList,
                      dataset: [
                        {
                          name: t(
                            'FinancialProfitabilityEfficiency.ParkingTurnoverRate',
                          ),
                          data: longTermData?.chargingTurnoverRate?.totalBillCountList?.map(
                            (item, idx) =>
                              Number(item) /
                              Number(
                                longTermData.chargingTurnoverRate
                                  ?.totalGunCountList[idx],
                              ),
                          ),
                          color: '#507ACD',
                        },
                      ],
                    }
              }
            />
          </MetricCard>
          <MetricCard
            title={t(
              'FinancialProfitabilityEfficiency.BusinessPerformanceFunnel',
            )}
            tooltipContent={
              <>
                {t('FinancialProfitabilityEfficiency.ChargingUtilizationRate')}
                <br />
                {t('FinancialProfitabilityEfficiency.ChargingPerformanceRate')}
                <br />
                {t('FinancialProfitabilityEfficiency.ChargingCollectionRate')}
              </>
            }
            position={{ left: '55%', top: '17%' }}
            ringColor={dataFlowColors.OperationalPerformanceFunnel}
            isLongterm
          >
            {!funnelOption ? (
              <div className="AreaChart__skeleton-wrapper">
                <Skeleton.Node active className="AreaChart__skeleton">
                  <FunnelPlotOutlined
                    style={{ fontSize: 156, color: '#bfbfbf' }}
                  />
                </Skeleton.Node>
              </div>
            ) : (
              <div style={{ width: '440px', height: '290px' }}>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={funnelOption}
                  containerProps={{
                    style: {
                      width: '400px',
                      height: '290px',
                    },
                  }}
                />
              </div>
            )}
          </MetricCard>
        </>
      )}
    </div>
  );
}

export default FinancialProfitabilityEfficiency;
